import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { Mono, useModeTokens } from '@/common/components/ui';

/** Horizontal page gutter. Every section uses this so the columns line up. */
export const GUTTER = { xs: '20px', sm: '28px', md: '40px' };

/**
 * The width the design composes at (`ScreenFrame`'s default). Content is capped
 * here and centred; the background stays full-bleed.
 */
export const CONTENT_MAX_WIDTH = 1420;

/** Mono rubric above a section headline. */
export const Eyebrow: React.FC<{
  children: React.ReactNode;
  mb: string;
  color?: string;
}> = ({ children, mb, color }) => {
  const t = useModeTokens();

  return (
    <Mono
      size={10}
      weight={600}
      letterSpacing="0.14em"
      color={color ?? t.colors.accentText}
      sx={{ display: 'block', marginBottom: mb }}
    >
      {children}
    </Mono>
  );
};

/**
 * The display headline shared by the lower marketing sections.
 *
 * Sized with `clamp` rather than a fixed pixel value: the design composes at a
 * fixed 1420px, but this page is the first thing a visitor sees on whatever
 * they happen to be holding, so the type has to travel.
 */
export const Headline: React.FC<{
  children: React.ReactNode;
  min?: number;
  max?: number;
  sx?: SxProps<Theme>;
}> = ({ children, min = 28, max = 42, sx }) => (
  <Box
    component="h2"
    sx={{
      margin: 0,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontWeight: 650,
      fontSize: `clamp(${min}px, 4.2vw, ${max}px)`,
      lineHeight: 1,
      letterSpacing: '-0.035em',
      ...sx,
    }}
  >
    {children}
  </Box>
);

/**
 * Horizontal rule that opens each lower section. The upper half of the page is
 * one continuous argument; below the fold the sections are separate claims, and
 * the rule is what marks that change of register.
 */
export const useSectionTop = (paddingTop: string) => {
  const t = useModeTokens();
  return { borderTop: `1px solid ${t.borders.divider}`, paddingTop };
};
